// JEE Mains PDF Scanner Component
// React component for scanning JEE Mains PDF files

import React, { useState, useRef } from 'react';
import LoadingSpinner from './LoadingSpinner.js';
import ErrorBoundary from './ErrorBoundary.js';

const JEEMainsPDFScannerComponent = () => {
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [pdfjsLib, setPdfjsLib] = useState(null);
  const fileInputRef = useRef(null);
  const scannerRef = useRef(null);

  // Load PDF.js library
  React.useEffect(() => {
    const loadPDFJS = async () => {
      try {
        // Dynamic import of PDF.js
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
        setPdfjsLib(pdfjs);
      } catch (err) {
        setError('Failed to load PDF.js library');
      }
    };

    loadPDFJS();
  }, []);

  // Handle file selection and scanning
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      scanPDF(file);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  // Scan PDF file
  const scanPDF = async (file) => {
    if (!pdfjsLib) {
      setError('PDF.js library not loaded');
      return;
    }

    setScanning(true);
    setError(null);
    setScanResults(null);

    try {
      // Scanner functionality removed - file not found
      setError('PDF Scanner functionality is currently unavailable');
    } catch (err) {
      setError(`Scan failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };


  // Export results
  const exportResults = (format) => {
    if (!scanResults || !scannerRef.current) return;
    
    try {
      const data = scannerRef.current.exportQuestions(format);
      const blob = new Blob([data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jee_mains_questions.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  // Reset scanner
  const resetScanner = () => {
    setScanResults(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <ErrorBoundary>
      <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            JEE Mains PDF Scanner
          </h1>
          <p className="text-gray-600 mb-6">
            Upload a JEE Mains PDF file to extract and analyze questions, answers, and metadata.
          </p>
        </div>

        {/* File Upload Section */}
        <div className="mb-8">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              disabled={scanning || !pdfjsLib}
              className="hidden"
              id="pdf-upload"
            />
            <label
              htmlFor="pdf-upload"
              className={`cursor-pointer ${scanning || !pdfjsLib ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              <div className="text-6xl text-gray-400 mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {scanning ? 'Scanning PDF...' : 'Upload JEE Mains PDF'}
              </h3>
              <p className="text-gray-500 mb-4">
                Click to select a PDF file or drag and drop it here
              </p>
              {!pdfjsLib && (
                <p className="text-red-500 text-sm">
                  Loading PDF.js library...
                </p>
              )}
            </label>
          </div>
          
        </div>

        {/* Loading State */}
        {scanning && (
          <div className="text-center py-8">
            <LoadingSpinner />
            <p className="text-gray-600 mt-4">Analyzing PDF content...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-400 mr-3">⚠️</div>
              <div>
                <h4 className="text-red-800 font-semibold">Scan Error</h4>
                <p className="text-red-600">{error}</p>
              </div>
            </div>
            <button
              onClick={resetScanner}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Scan Results */}
        {scanResults && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-blue-800 mb-4">Scan Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {scanResults.totalQuestions}
                  </div>
                  <div className="text-sm text-gray-600">Questions Found</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">
                    {scanResults.totalPages}
                  </div>
                  <div className="text-sm text-gray-600">Pages Scanned</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {scanResults.subjects.length}
                  </div>
                  <div className="text-sm text-gray-600">Subjects</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-2xl font-bold text-orange-600">
                    {scanResults.difficulty}
                  </div>
                  <div className="text-sm text-gray-600">Difficulty</div>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">Exam Details</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>Year: {scanResults.year || 'Unknown'}</li>
                    <li>Shift: {scanResults.shift || 'Unknown'}</li>
                    <li>Subjects: {scanResults.subjects.join(', ')}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">Scan Details</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>Method: {scanResults.metadata.extractionMethod}</li>
                    <li>Confidence: {Math.round(scanResults.metadata.confidence * 100)}%</li>
                    <li>Text Length: {scanResults.metadata.textLength.toLocaleString()} chars</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Export Results</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => exportResults('json')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => exportResults('csv')}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => exportResults('markdown')}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Export Markdown
                </button>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  Extracted Questions ({scanResults.questions.length})
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {scanResults.questions.map((question, index) => (
                  <div key={question.id || index} className="p-4 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          Q{question.number}
                        </span>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          {question.subject}
                        </span>
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                          {question.difficulty}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {question.topic}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-gray-800 font-medium">{question.question}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center">
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm mr-2">
                            {optIndex + 1}
                          </span>
                          <span className="text-sm text-gray-600">{option}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Answer:</span>
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                          {question.correctAnswer}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {question.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reset Button */}
            <div className="text-center">
              <button
                onClick={resetScanner}
                className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Scan Another PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default JEEMainsPDFScannerComponent;
